import React, { useEffect, useState } from "react";
import Page from "../../components/Page";
import { 
  IconBluetooth, 
  IconRefresh, 
  IconSearch, 
  IconAlertCircle, 
  IconCheck, 
  IconServer, 
  IconInfoCircle, 
  IconDeviceHeartMonitor,
  IconWifi,
  IconPlus,
  IconTrash,
  IconPrinter,
  IconSend,
  IconNetwork,
  IconDeviceDesktop,
  IconStar,
  IconChecklist,
  IconPencil,
  IconAdjustments
} from "@tabler/icons-react";
import { iconStroke } from "../../config/config";
import toast from "react-hot-toast";
import { 
  fetchPrintersFromWebSocketAgent, 
  scanNetworkPrintersFromWebSocketAgent,
  setBluetoothDevice, 
  getBluetoothDevice, 
  printViaWebBluetooth,
  getNetworkPrinters,
  saveNetworkPrinter,
  deleteNetworkPrinter,
  testPrinter,
  printKOT,
  getPrinterTypes,
  savePrinterType,
  deletePrinterType,
  getCategoryAssignments,
  saveCategoryAssignment,
  DEFAULT_PRINTER_TYPE_ID
} from "../../helpers/PrinterHelper";
import { useCategories, updateCategory } from "../../controllers/settings.controller";
import { mutate } from "swr";

const SafeIconPrinter = ({ size = 24, stroke = 2, className = "", ...props }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" className={className} {...props}>
    <path d="M17 17h2a2 2 0 0 0 2 -2v-4a2 2 0 0 0 -2 -2h-14a2 2 0 0 0 -2 2v4a2 2 0 0 0 2 2h2" />
    <path d="M17 9v-4a2 2 0 0 0 -2 -2h-6a2 2 0 0 0 -2 2v4" />
    <path d="M7 13m0 2a2 2 0 0 1 2 -2h6a2 2 0 0 1 2 2v4a2 2 0 0 1 -2 2h-6a2 2 0 0 1 -2 -2z" />
  </svg>
);

const isElectron = typeof window !== 'undefined' && Boolean(
  window.isElectron ||
  window.electron ||
  (window.process && window.process.type) ||
  (window.require && typeof window.require === 'function')
);

export default function PrintersPage() {
  const [activeTab, setActiveTab] = useState("types"); // "types" | "categories" | "hardware"
  const [printers, setPrinters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [testingPrinter, setTestingPrinter] = useState(null);

  // Printer Types State (matching Flutter KDS)
  const [printerTypes, setPrinterTypes] = useState(getPrinterTypes());
  const [categoryAssignments, setCategoryAssignments] = useState(getCategoryAssignments());
  const [showPrinterTypeModal, setShowPrinterTypeModal] = useState(false);
  const [editingPrinterType, setEditingPrinterType] = useState(null);
  const [typeForm, setTypeForm] = useState({
    id: "",
    name: "",
    isOsPrinter: true,
    osPrinterName: "",
    ipPrinterAddress: "",
    copies: 1,
    paperFormat: "80"
  });

  // Network / WiFi Printers
  const [networkPrinters, setNetworkPrinters] = useState(getNetworkPrinters());
  const [scanningNetwork, setScanningNetwork] = useState(false);
  const [showAddIpModal, setShowAddIpModal] = useState(false);
  const [ipForm, setIpForm] = useState({ name: "", ip: "", port: "9100", format: "80" });
  const [verifyingIp, setVerifyingIp] = useState(false);

  // Default Bill Printer State
  const [defaultBillPrinter, setDefaultBillPrinter] = useState(localStorage.getItem("billPrinter") || "");
  const [pageFormat, setPageFormat] = useState(localStorage.getItem("page_format") || "80");
  const [billCopies, setBillCopies] = useState(parseInt(localStorage.getItem("printCount")) || 1);

  // Category KOT Routing Data
  const { APIURL: categoriesApiUrl, data: categories = [], isLoading: categoriesLoading } = useCategories();
  const [savingCategory, setSavingCategory] = useState(null);

  // Bluetooth Printer States
  const [btConnected, setBtConnected] = useState(!!getBluetoothDevice());
  const [btDeviceName, setBtDeviceName] = useState(getBluetoothDevice()?.name || "");

  // Web Agent States
  const [agentUrl, setAgentUrl] = useState(localStorage.getItem("printer_agent_url") || "ws://127.0.0.1:8124");
  const [agentStatus, setAgentStatus] = useState("disconnected");

  useEffect(() => {
    if (Array.isArray(categories) && categories.length > 0) {
      try { localStorage.setItem('cached_categories', JSON.stringify(categories)); } catch(e) {}
    }
  }, [categories]);

  // Fetch local OS printers
  const fetchPrinters = async () => {
    setLoading(true);
    if (isElectron) {
      try {
        let list = [];
        if (window.electron && window.electron.getPrinters) {
          list = await window.electron.getPrinters();
        } else if (window.require) {
          const { ipcRenderer } = window.require('electron');
          list = await ipcRenderer.invoke('get-printers');
        }
        setPrinters(list || []);
        toast.success("Printers list refreshed");
      } catch (error) {
        console.error(error);
        toast.error("Failed to fetch OS printers");
      } finally {
        setLoading(false);
      }
    } else {
      // Browser mode: fetch via WebSocket Agent
      setAgentStatus("checking");
      try {
        const isConnected = await checkAgentConnection(agentUrl);
        if (isConnected) {
          setAgentStatus("connected");
          const list = await fetchPrintersFromWebSocketAgent();
          setPrinters(list || []);
          toast.success("Printers discovered via Web Agent!");
        } else {
          setAgentStatus("disconnected");
          setPrinters([]);
          toast.error("Could not connect to WebSocket Print Agent.");
        }
      } catch (error) {
        console.error(error);
        setAgentStatus("disconnected");
        setPrinters([]);
        toast.error("Failed to fetch printers: " + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  // Scan network subnet for thermal printers
  const scanNetworkPrinters = async () => {
    setScanningNetwork(true);
    toast.loading("Scanning local network for thermal printers (Port 9100)...");
    try {
      const discovered = await scanNetworkPrintersFromWebSocketAgent();
      toast.dismiss();
      if (discovered && discovered.length > 0) {
        setNetworkPrinters(getNetworkPrinters());
        toast.success(`Found ${discovered.length} network thermal printer(s)!`);
      } else {
        toast("No network printers found. You can add your printer manually via 'Add IP Printer'.", { icon: "ℹ️" });
      }
    } catch (e) {
      toast.dismiss();
      toast.error(`Network scan failed: ${e.message}`);
    } finally {
      setScanningNetwork(false);
    }
  };

  // Add / Edit Printer Type Handlers
  const handleOpenAddPrinterType = () => {
    setEditingPrinterType(null);
    setTypeForm({
      id: `pt_${Date.now()}`,
      name: "",
      isOsPrinter: true,
      osPrinterName: printers[0]?.name || defaultBillPrinter || "",
      ipPrinterAddress: "",
      copies: 1,
      paperFormat: pageFormat || "80"
    });
    setShowPrinterTypeModal(true);
  };

  const handleOpenEditPrinterType = (pt) => {
    setEditingPrinterType(pt);
    setTypeForm({
      id: pt.id,
      name: pt.name,
      isOsPrinter: pt.isOsPrinter ?? true,
      osPrinterName: pt.osPrinterName || "",
      ipPrinterAddress: pt.ipPrinterAddress || "",
      copies: pt.copies || 1,
      paperFormat: pt.paperFormat || "80"
    });
    setShowPrinterTypeModal(true);
  };

  const handleSavePrinterType = () => {
    if (!typeForm.name.trim() && typeForm.id !== DEFAULT_PRINTER_TYPE_ID) {
      toast.error("Please enter a Printer Type name (e.g. Kitchen Veg, Kitchen Non-Veg, Bar)");
      return;
    }
    savePrinterType(typeForm);
    setPrinterTypes(getPrinterTypes());
    setShowPrinterTypeModal(false);
    toast.success(`Saved Printer Type "${typeForm.name || 'Default Printer'}"!`);
  };

  const handleDeletePrinterType = (id, name) => {
    if (id === DEFAULT_PRINTER_TYPE_ID) return;
    if (window.confirm(`Are you sure you want to delete printer type "${name}"? Categories assigned to it will revert to Default.`)) {
      deletePrinterType(id);
      setPrinterTypes(getPrinterTypes());
      setCategoryAssignments(getCategoryAssignments());
      toast.success(`Deleted printer type "${name}"`);
    }
  };

  // Test Print a specific Printer Type
  const handleTestPrinterType = async (pt) => {
    setTestingPrinter(pt.id);
    toast.loading(`Sending test KOT to "${pt.name}" (${pt.copies}x)...`);
    try {
      const mockItems = [
        {
          id: 991,
          title: `Test Item (${pt.name})`,
          quantity: 1,
          notes: `Test print with ${pt.copies} copy (${pt.paperFormat}mm roll)`,
          addons: []
        }
      ];

      const printerGroups = {
        [pt.id]: {
          printerTypeId: pt.id,
          printerTypeName: pt.name,
          printerName: pt.isOsPrinter ? (pt.osPrinterName || defaultBillPrinter || "Default") : (pt.ipPrinterAddress ? `IP:${pt.ipPrinterAddress}` : (pt.osPrinterName || "Default")),
          isOsPrinter: pt.isOsPrinter,
          ipPrinterAddress: pt.ipPrinterAddress,
          copies: pt.copies || 1,
          paperFormat: pt.paperFormat || "80",
          items: mockItems
        }
      };

      const orderDetails = {
        orderId: `TEST-${Math.floor(100 + Math.random() * 900)}`,
        tokenNo: "TEST",
        table_title: "Test Table",
        waiterName: "Admin",
        printerTypeName: pt.name,
        pageFormat: pt.paperFormat || "80"
      };

      await printKOT(printerGroups, orderDetails);
      toast.dismiss();
      toast.success(`Test KOT printed successfully for "${pt.name}"!`);
    } catch (err) {
      toast.dismiss();
      console.error(err);
      toast.error(`Print failed: ${err.message}`);
    } finally {
      setTestingPrinter(null);
    }
  };

  // Manual IP Add & Verify
  const handleVerifyAndSaveIpPrinter = async () => {
    if (!ipForm.ip.trim()) {
      toast.error("Please enter a valid IP address");
      return;
    }
    setVerifyingIp(true);
    toast.loading(`Connecting to printer at ${ipForm.ip}:${ipForm.port || 9100}...`);

    try {
      const printerObj = {
        name: ipForm.name.trim() || `Network Printer (${ipForm.ip})`,
        ip: ipForm.ip.trim(),
        port: parseInt(ipForm.port) || 9100,
        format: ipForm.format || "80"
      };

      await testPrinter(printerObj);
      saveNetworkPrinter(printerObj);
      setNetworkPrinters(getNetworkPrinters());
      setShowAddIpModal(false);
      setIpForm({ name: "", ip: "", port: "9100", format: "80" });
      toast.dismiss();
      toast.success(`Connected & saved "${printerObj.name}"! Test slip printed.`);
    } catch (err) {
      toast.dismiss();
      console.error(err);
      if (window.confirm(`Could not verify printer at ${ipForm.ip}: ${err.message}\n\nDo you want to save it anyway?`)) {
        const printerObj = {
          name: ipForm.name.trim() || `Network Printer (${ipForm.ip})`,
          ip: ipForm.ip.trim(),
          port: parseInt(ipForm.port) || 9100,
          format: ipForm.format || "80"
        };
        saveNetworkPrinter(printerObj);
        setNetworkPrinters(getNetworkPrinters());
        setShowAddIpModal(false);
        setIpForm({ name: "", ip: "", port: "9100", format: "80" });
        toast.success(`Saved "${printerObj.name}".`);
      }
    } finally {
      setVerifyingIp(false);
    }
  };

  const handleDeleteNetworkPrinter = (id, name) => {
    if (window.confirm(`Remove network printer "${name}"?`)) {
      deleteNetworkPrinter(id);
      setNetworkPrinters(getNetworkPrinters());
      toast.success("Removed printer");
    }
  };

  const handleTestPrint = async (printerObj) => {
    setTestingPrinter(printerObj.isIp ? `net_${printerObj.ip}` : printerObj.name);
    toast.loading(`Sending test page to "${printerObj.name}"...`);
    try {
      await testPrinter(printerObj);
      toast.dismiss();
      toast.success(`Test page printed to "${printerObj.name}"!`);
    } catch (err) {
      toast.dismiss();
      console.error(err);
      toast.error(`Print failed: ${err.message}`);
    } finally {
      setTestingPrinter(null);
    }
  };

  // Save Default Bill Printer
  const handleSetDefaultBillPrinter = (printerName) => {
    localStorage.setItem("billPrinter", printerName);
    setDefaultBillPrinter(printerName);
    // Sync default printer type
    const defType = printerTypes.find(p => p.id === DEFAULT_PRINTER_TYPE_ID);
    if (defType) {
      savePrinterType({ ...defType, osPrinterName: printerName });
      setPrinterTypes(getPrinterTypes());
    }
    toast.success(`Default Bill Printer set to "${printerName || 'Default'}"`);
  };

  const handleSetBillCopies = (val) => {
    const c = Math.max(1, parseInt(val) || 1);
    setBillCopies(c);
    localStorage.setItem("printCount", c);
    const defType = printerTypes.find(p => p.id === DEFAULT_PRINTER_TYPE_ID);
    if (defType) {
      savePrinterType({ ...defType, copies: c });
      setPrinterTypes(getPrinterTypes());
    }
    toast.success(`Bill copies set to ${c}`);
  };

  // Assign Printer Type to Category (Matching Flutter KDS saveAssignment)
  const handleAssignCategoryPrinterType = async (category, printerTypeId) => {
    setSavingCategory(category.id);
    toast.loading(`Assigning printer type to ${category.title}...`);
    try {
      saveCategoryAssignment(category.id, printerTypeId);
      setCategoryAssignments(getCategoryAssignments());

      // Update backend category so it syncs across devices
      const res = await updateCategory(category.id, category.title, printerTypeId);
      toast.dismiss();
      if (res.status === 200) {
        await mutate(categoriesApiUrl);
        const assignedType = printerTypes.find(pt => pt.id === printerTypeId) || { name: "Default Printer" };
        toast.success(`Assigned ${category.title} to "${assignedType.name}"!`);
      } else {
        toast.error("Failed to update category printer in database");
      }
    } catch (err) {
      toast.dismiss();
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to update category printer");
    } finally {
      setSavingCategory(null);
    }
  };

  // Send Sample Test KOT to Category
  const handleTestCategoryKOT = async (category) => {
    const assignedTypeId = categoryAssignments[category.id] || (printerTypes.some(p => p.id === category.printer) ? category.printer : DEFAULT_PRINTER_TYPE_ID);
    const targetType = printerTypes.find(pt => pt.id === assignedTypeId) || printerTypes.find(pt => pt.id === DEFAULT_PRINTER_TYPE_ID) || printerTypes[0];

    toast.loading(`Sending sample KOT for "${category.title}" via [${targetType.name}]...`);
    try {
      const mockItems = [
        {
          id: 991,
          category_id: category.id,
          category_title: category.title,
          title: `${category.title} Special Item`,
          quantity: 2,
          notes: "Hot & Spicy / Less Oil",
          addons: []
        }
      ];

      const printerGroups = {
        [targetType.id]: {
          printerTypeId: targetType.id,
          printerTypeName: targetType.name,
          printerName: targetType.isOsPrinter ? (targetType.osPrinterName || defaultBillPrinter || "Default") : (targetType.ipPrinterAddress ? `IP:${targetType.ipPrinterAddress}` : (targetType.osPrinterName || "Default")),
          isOsPrinter: targetType.isOsPrinter,
          ipPrinterAddress: targetType.ipPrinterAddress,
          copies: targetType.copies || 1,
          paperFormat: targetType.paperFormat || "80",
          items: mockItems
        }
      };

      const orderDetails = {
        orderId: `TEST-${Math.floor(100 + Math.random() * 900)}`,
        tokenNo: "TEST",
        table_title: "Table 1 (TEST)",
        waiterName: "Admin Test",
        printerTypeName: targetType.name,
        pageFormat: targetType.paperFormat || "80"
      };

      await printKOT(printerGroups, orderDetails);
      toast.dismiss();
      toast.success(`Sample KOT printed for "${category.title}" via [${targetType.name}]!`);
    } catch (err) {
      toast.dismiss();
      console.error(err);
      toast.error(`KOT print failed: ${err.message}`);
    }
  };

  // Quick WebSocket check for browser
  const checkAgentConnection = (urlToTest) => {
    return new Promise((resolve) => {
      try {
        const ws = new WebSocket(urlToTest);
        const timer = setTimeout(() => {
          ws.close();
          resolve(false);
        }, 1500);

        ws.onopen = () => ws.send(JSON.stringify({ type: "ping" }));
        ws.onmessage = (event) => {
          clearTimeout(timer);
          try {
            const res = JSON.parse(event.data);
            resolve(res.type === "pong" && res.status === "connected");
          } catch (e) {
            resolve(false);
          } finally {
            ws.close();
          }
        };
        ws.onerror = () => {
          clearTimeout(timer);
          resolve(false);
        };
      } catch (err) {
        resolve(false);
      }
    });
  };

  // Web Bluetooth handler
  const connectBluetoothPrinter = async () => {
    try {
      if (!navigator.bluetooth) {
        toast.error("Web Bluetooth API is not supported in this browser.");
        return;
      }
      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: ['000018f0-0000-1000-8000-00805f9b34fb']
      });

      toast.loading("Connecting to GATT server...");
      const server = await device.gatt.connect();
      const service = await server.getPrimaryService('000018f0-0000-1000-8000-00805f9b34fb');
      const characteristic = await service.getCharacteristic('00002af1-0000-1000-8000-00805f9b34fb');

      setBluetoothDevice(device, characteristic);
      setBtConnected(true);
      setBtDeviceName(device.name || "Bluetooth Printer");
      toast.dismiss();
      toast.success(`Connected to ${device.name || "Bluetooth Printer"}`);

      device.addEventListener('gattserverdisconnected', () => {
        setBluetoothDevice(null, null);
        setBtConnected(false);
        setBtDeviceName("");
        toast.error("Bluetooth printer disconnected");
      });
    } catch (error) {
      toast.dismiss();
      console.error(error);
      toast.error(`Failed to connect: ${error.message}`);
    }
  };

  useEffect(() => {
    fetchPrinters();
  }, []);

  return (
    <Page className="px-8 py-6 text-restro-green-dark">
      {/* Page Title & Navigation Tabs */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="text-3xl font-light flex items-center gap-2">
            <SafeIconPrinter size={32} /> Printers & KOT Routing
          </h3>
          <p className="text-sm text-gray-500">
            Create Printer Types (e.g. Veg, Non-Veg, Bar), assign them to menu categories, and route KOT slips automatically.
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex items-center gap-1.5 bg-gray-100 dark:bg-black/40 p-1.5 rounded-xl border border-restro-border-green">
          <button
            onClick={() => setActiveTab("types")}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition ${
              activeTab === "types"
                ? "bg-restro-green text-white shadow"
                : "text-gray-600 dark:text-gray-300 hover:text-restro-green"
            }`}
          >
            <span className="flex items-center gap-1.5">
              <SafeIconPrinter size={18} /> Printer Types
            </span>
          </button>
          <button
            onClick={() => setActiveTab("categories")}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition ${
              activeTab === "categories"
                ? "bg-restro-green text-white shadow"
                : "text-gray-600 dark:text-gray-300 hover:text-restro-green"
            }`}
          >
            <span className="flex items-center gap-1.5">
              <IconChecklist size={18} /> Category KOT Routing
            </span>
          </button>
          <button
            onClick={() => setActiveTab("hardware")}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition ${
              activeTab === "hardware"
                ? "bg-restro-green text-white shadow"
                : "text-gray-600 dark:text-gray-300 hover:text-restro-green"
            }`}
          >
            <span className="flex items-center gap-1.5">
              <IconDeviceDesktop size={18} /> Discovered Devices
            </span>
          </button>
        </div>
      </div>

      {/* ───────────────────────────────────────────────────────────── */}
      {/* TAB 1: PRINTER TYPES (MAIN ROUTING SYSTEM)                    */}
      {/* ───────────────────────────────────────────────────────────── */}
      {activeTab === "types" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h4 className="text-2xl font-bold flex items-center gap-2">
                <SafeIconPrinter size={26} className="text-restro-green" /> Configured Printer Types
              </h4>
              <p className="text-xs text-gray-500 mt-0.5">
                Create printer types (e.g. <b>Kitchen Veg</b>, <b>Kitchen Non-Veg</b>, <b>Bar Station</b>) and assign physical printers to them.
                You can map multiple types to the <b>same physical printer</b> to automatically print separate KOT slips per category!
              </p>
            </div>

            <button
              onClick={handleOpenAddPrinterType}
              className="flex items-center gap-2 btn btn-sm rounded-xl bg-restro-green hover:bg-restro-green/90 text-white font-semibold shadow px-4 py-2"
            >
              <IconPlus size={18} /> Add Printer Type
            </button>
          </div>

          {/* Printer Types Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {printerTypes.map((pt) => {
              const isDefault = pt.id === DEFAULT_PRINTER_TYPE_ID;
              const isTesting = testingPrinter === pt.id;
              const destinationLabel = pt.isOsPrinter 
                ? (pt.osPrinterName ? `OS: ${pt.osPrinterName}` : "System Default Printer")
                : (pt.ipPrinterAddress ? `WiFi IP: ${pt.ipPrinterAddress}` : "IP: Not configured");

              return (
                <div
                  key={pt.id}
                  className={`p-5 bg-white dark:bg-restro-gray border rounded-2xl flex flex-col justify-between gap-4 shadow-sm transition hover:shadow-md ${
                    isDefault ? "border-emerald-500 ring-2 ring-emerald-500/20" : "border-restro-border-green"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-xl bg-emerald-50 dark:bg-emerald-950/50 flex items-center justify-center text-emerald-600 flex-shrink-0">
                      <SafeIconPrinter size={26} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1">
                        <h4 className="font-bold truncate text-lg" title={pt.name}>
                          {pt.name}
                        </h4>
                        {isDefault && (
                          <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900 text-emerald-800 dark:text-emerald-200">
                            Default
                          </span>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-1.5 mt-2">
                        <span className="text-[11px] font-bold px-2 py-0.5 rounded-md bg-gray-100 dark:bg-black/40 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-800">
                          {pt.copies || 1} {pt.copies === 1 ? "Copy" : "Copies"}
                        </span>
                        <span className="text-[11px] font-bold px-2 py-0.5 rounded-md bg-teal-50 dark:bg-teal-950/40 text-teal-700 dark:text-teal-300 border border-teal-200 dark:border-teal-800">
                          {pt.paperFormat || "80"}mm Roll
                        </span>
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-md bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                          {pt.isOsPrinter ? "OS/USB" : "WiFi IP"}
                        </span>
                      </div>

                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 font-mono truncate" title={destinationLabel}>
                        🎯 {destinationLabel}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-3 border-t border-gray-100 dark:border-gray-800">
                    <button
                      onClick={() => handleTestPrinterType(pt)}
                      disabled={isTesting}
                      className="flex-1 btn btn-outline btn-xs rounded-lg text-emerald-700 border-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950 font-semibold flex items-center justify-center gap-1"
                    >
                      <IconSend size={14} className={isTesting ? "animate-spin" : ""} />
                      {isTesting ? "Printing..." : `Test Print (${pt.copies || 1}x)`}
                    </button>

                    <button
                      onClick={() => handleOpenEditPrinterType(pt)}
                      className="btn btn-outline btn-xs rounded-lg text-blue-600 border-blue-300 hover:bg-blue-50 font-semibold flex items-center gap-1"
                      title="Edit Printer Type"
                    >
                      <IconPencil size={14} /> Edit
                    </button>

                    {!isDefault && (
                      <button
                        onClick={() => handleDeletePrinterType(pt.id, pt.name)}
                        className="btn btn-ghost btn-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-950 rounded-lg p-1"
                        title="Delete Printer Type"
                      >
                        <IconTrash size={16} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Default Bill Printer Selection Card */}
          <div className="p-6 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/20 border border-emerald-200 dark:border-emerald-800 rounded-2xl flex flex-col md:flex-row md:items-center justify-between gap-4 mt-8">
            <div>
              <div className="flex items-center gap-2 text-emerald-800 dark:text-emerald-300 font-bold mb-1">
                <IconStar size={20} className="fill-emerald-500 text-emerald-500" /> Default Bill / Receipt Printer
              </div>
              <p className="text-xs text-emerald-700 dark:text-emerald-400">
                Used for printing customer receipts, estimates, invoices, and fallback KOTs.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <select
                value={defaultBillPrinter}
                onChange={(e) => handleSetDefaultBillPrinter(e.target.value)}
                className="text-sm rounded-lg px-4 py-2 border border-emerald-300 dark:border-emerald-700 bg-white dark:bg-black font-semibold text-gray-800 dark:text-gray-200 focus:outline-none"
              >
                <option value="">Default (System Default)</option>
                <optgroup label="OS Installed Printers">
                  {printers.map((p, idx) => (
                    <option key={`os-${idx}`} value={p.name}>
                      {p.displayName || p.name}
                    </option>
                  ))}
                </optgroup>
                {networkPrinters.length > 0 && (
                  <optgroup label="WiFi / Network Printers">
                    {networkPrinters.map((np, idx) => (
                      <option key={`net-${idx}`} value={`IP:${np.ip}:${np.port}`}>
                        {np.name} (WiFi: {np.ip}:{np.port})
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>

              <select
                value={pageFormat}
                onChange={(e) => {
                  setPageFormat(e.target.value);
                  localStorage.setItem("page_format", e.target.value);
                  toast.success(`Paper format set to ${e.target.value}mm`);
                }}
                className="text-sm rounded-lg px-3 py-2 border border-emerald-300 dark:border-emerald-700 bg-white dark:bg-black font-semibold text-gray-800 dark:text-gray-200"
              >
                <option value="58">58mm Roll</option>
                <option value="80">80mm Roll</option>
              </select>

              <select
                value={billCopies}
                onChange={(e) => handleSetBillCopies(e.target.value)}
                className="text-sm rounded-lg px-3 py-2 border border-emerald-300 dark:border-emerald-700 bg-white dark:bg-black font-semibold text-gray-800 dark:text-gray-200"
                title="Number of copies printed per customer bill"
              >
                <option value="1">1 Bill Copy</option>
                <option value="2">2 Bill Copies</option>
                <option value="3">3 Bill Copies</option>
                <option value="4">4 Bill Copies</option>
              </select>

              {defaultBillPrinter && (
                <button
                  onClick={() => handleTestPrint({
                    name: defaultBillPrinter,
                    isIp: defaultBillPrinter.startsWith("IP:"),
                    ip: defaultBillPrinter.replace(/^IP:/i, '').split(':')[0],
                    port: defaultBillPrinter.includes(':') ? parseInt(defaultBillPrinter.split(':')[1]) : 9100,
                    format: pageFormat
                  })}
                  disabled={testingPrinter === defaultBillPrinter}
                  className="btn btn-sm rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1 shadow"
                >
                  <IconSend size={16} /> Test Default
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ───────────────────────────────────────────────────────────── */}
      {/* TAB 2: CATEGORY KOT ROUTING                                  */}
      {/* ───────────────────────────────────────────────────────────── */}
      {activeTab === "categories" && (
        <div className="space-y-6">
          <div className="p-5 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/20 border border-blue-200 dark:border-blue-800 rounded-2xl flex items-start gap-3">
            <IconInfoCircle size={24} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-bold text-blue-900 dark:text-blue-300 text-base">
                Category-to-Printer Type Routing
              </h4>
              <p className="text-sm text-blue-800 dark:text-blue-400 mt-1">
                Assign menu item categories to specific <b>Printer Types</b>. For example, assign <i>Veg Chowmein</i> category to <b>Kitchen Veg</b> and <i>Chicken Chowmein</i> category to <b>Kitchen Non-Veg</b>.
                Even if both Printer Types map to the <b>same physical printer</b>, separate KOT slips will be generated and printed automatically when new orders arrive via WebSocket or when punching KOTs on POS!
              </p>
            </div>
          </div>

          <div className="overflow-x-auto border border-restro-border-green rounded-2xl bg-white dark:bg-restro-gray shadow-sm">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 dark:bg-black/30 border-b border-restro-border-green text-gray-500 font-semibold text-xs uppercase tracking-wider">
                  <th className="px-4 py-3 w-16">#</th>
                  <th className="px-4 py-3">Category Title</th>
                  <th className="px-4 py-3">Assigned Printer Type</th>
                  <th className="px-4 py-3">Physical Target & Copies</th>
                  <th className="px-4 py-3 w-36 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-sm">
                {categoriesLoading ? (
                  <tr>
                    <td colSpan={5} className="text-center py-8 text-gray-500">
                      Loading categories...
                    </td>
                  </tr>
                ) : categories.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-8 text-gray-500">
                      No categories found. Create categories first in Settings &gt; Categories.
                    </td>
                  </tr>
                ) : (
                  categories.map((cat, idx) => {
                    const isSaving = savingCategory === cat.id;
                    const assignedTypeId = categoryAssignments[cat.id] || (printerTypes.some(p => p.id === cat.printer) ? cat.printer : DEFAULT_PRINTER_TYPE_ID);
                    const targetType = printerTypes.find(pt => pt.id === assignedTypeId) || printerTypes.find(pt => pt.id === DEFAULT_PRINTER_TYPE_ID) || printerTypes[0];

                    return (
                      <tr key={cat.id} className="hover:bg-gray-50/50 dark:hover:bg-black/20 transition">
                        <td className="px-4 py-3.5 font-medium text-gray-400">{idx + 1}</td>
                        <td className="px-4 py-3.5 font-bold text-gray-900 dark:text-gray-100">
                          {cat.title}
                          {!cat.is_enabled && (
                            <span className="ml-2 text-[10px] px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                              Hidden
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-2">
                            <select
                              value={assignedTypeId}
                              onChange={(e) => handleAssignCategoryPrinterType(cat, e.target.value)}
                              disabled={isSaving}
                              className="text-sm rounded-lg px-3 py-1.5 border border-restro-border-green bg-white dark:bg-black font-semibold text-gray-800 dark:text-gray-200 focus:outline-restro-border-green w-full max-w-sm"
                            >
                              {printerTypes.map((pt) => {
                                const targetDesc = pt.isOsPrinter ? (pt.osPrinterName || "OS Default") : (pt.ipPrinterAddress || "WiFi IP");
                                return (
                                  <option key={pt.id} value={pt.id}>
                                    🖨️ {pt.name} ({targetDesc} • {pt.copies || 1}x)
                                  </option>
                                );
                              })}
                            </select>
                            {assignedTypeId && assignedTypeId !== DEFAULT_PRINTER_TYPE_ID && (
                              <span className="text-xs text-emerald-600 font-bold flex items-center gap-0.5">
                                <IconCheck size={16} /> Saved
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-2">
                            <span className="text-xs px-2.5 py-1 rounded-md bg-gray-100 dark:bg-black/40 text-gray-700 dark:text-gray-300 font-mono font-medium border border-gray-200 dark:border-gray-800">
                              {targetType.isOsPrinter ? (targetType.osPrinterName || "OS Default") : (targetType.ipPrinterAddress || "IP: Not set")}
                            </span>
                            <span className="text-xs font-bold text-emerald-600">
                              {targetType.copies || 1} {targetType.copies === 1 ? "Copy" : "Copies"}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3.5 text-center">
                          <button
                            onClick={() => handleTestCategoryKOT(cat)}
                            className="btn btn-outline btn-xs rounded-lg text-restro-green-dark border-restro-border-green hover:bg-restro-button-hover font-semibold inline-flex items-center gap-1"
                            title={`Send test KOT for ${cat.title} via ${targetType.name}`}
                          >
                            <IconSend size={12} /> Test KOT
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ───────────────────────────────────────────────────────────── */}
      {/* TAB 3: DISCOVERED HARDWARE (OS PRINTERS & NETWORK PRINTERS)   */}
      {/* ───────────────────────────────────────────────────────────── */}
      {activeTab === "hardware" && (
        <div className="space-y-8">
          {/* SECTION: WiFi / Network Printers */}
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
              <div>
                <h4 className="text-xl font-bold flex items-center gap-2">
                  <IconWifi size={24} className="text-teal-600" /> WiFi & LAN Network Printers (Port 9100)
                </h4>
                <p className="text-xs text-gray-500">
                  Connect wireless and Ethernet thermal printers directly without installing OS drivers.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={scanNetworkPrinters}
                  disabled={scanningNetwork}
                  className="flex items-center gap-2 btn btn-outline btn-sm rounded-lg border-teal-500 text-teal-700 hover:bg-teal-50 dark:hover:bg-teal-950 font-semibold"
                >
                  <IconWifi size={16} className={scanningNetwork ? "animate-pulse text-teal-600" : ""} />
                  {scanningNetwork ? "Scanning Subnet..." : "Discover WiFi Printers"}
                </button>
                <button
                  onClick={() => setShowAddIpModal(true)}
                  className="flex items-center gap-2 btn btn-sm rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-semibold shadow"
                >
                  <IconPlus size={16} /> Add IP Printer
                </button>
              </div>
            </div>

            {/* Network Printers Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {networkPrinters.length === 0 ? (
                <div className="col-span-full text-center py-8 bg-gray-50 dark:bg-black/30 rounded-2xl border border-dashed border-gray-300">
                  <IconWifi size={36} className="mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-500 font-medium">No WiFi/Network printers configured yet.</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Click <b>Discover WiFi Printers</b> to auto-scan your subnet, or <b>Add IP Printer</b> to configure one manually.
                  </p>
                </div>
              ) : (
                networkPrinters.map((np) => {
                  const isCurrentDefault = defaultBillPrinter === `IP:${np.ip}:${np.port}`;
                  const isTesting = testingPrinter === `net_${np.ip}`;
                  return (
                    <div
                      key={np.id}
                      className={`p-5 bg-white dark:bg-restro-gray border rounded-2xl flex flex-col justify-between gap-4 shadow-sm transition hover:shadow-md ${
                        isCurrentDefault ? "border-emerald-500 ring-2 ring-emerald-500/20" : "border-restro-border-green"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-xl bg-teal-50 dark:bg-teal-950/50 flex items-center justify-center text-teal-600 flex-shrink-0">
                          <IconWifi size={24} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-1">
                            <h4 className="font-bold truncate text-base" title={np.name}>{np.name}</h4>
                            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-teal-100 dark:bg-teal-900 text-teal-800 dark:text-teal-200">
                              {np.format || "80"}mm
                            </span>
                          </div>
                          <div className="text-xs font-mono text-gray-600 dark:text-gray-300 mt-0.5">
                            {np.ip}:{np.port || 9100}
                          </div>
                          {isCurrentDefault && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600 mt-1">
                              <IconStar size={12} className="fill-emerald-500" /> Default Bill Printer
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 pt-2 border-t border-gray-100 dark:border-gray-800">
                        <button
                          onClick={() => handleTestPrint({
                            name: np.name,
                            isIp: true,
                            ip: np.ip,
                            port: np.port,
                            format: np.format
                          })}
                          disabled={isTesting}
                          className="flex-1 btn btn-outline btn-xs rounded-lg text-teal-700 border-teal-300 hover:bg-teal-50 dark:hover:bg-teal-900 font-semibold flex items-center justify-center gap-1"
                        >
                          <IconSend size={14} className={isTesting ? "animate-spin" : ""} />
                          {isTesting ? "Printing..." : "Test Print"}
                        </button>

                        {!isCurrentDefault ? (
                          <button
                            onClick={() => handleSetDefaultBillPrinter(`IP:${np.ip}:${np.port}`)}
                            className="btn btn-outline btn-xs rounded-lg text-emerald-700 border-emerald-300 hover:bg-emerald-50 font-semibold"
                            title="Set as Default Bill Printer"
                          >
                            Set Default
                          </button>
                        ) : null}

                        <button
                          onClick={() => handleDeleteNetworkPrinter(np.id, np.name)}
                          className="btn btn-ghost btn-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-950 rounded-lg p-1"
                          title="Remove Printer"
                        >
                          <IconTrash size={16} />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* SECTION: Discovered OS Printers */}
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
              <div>
                <h4 className="text-xl font-bold flex items-center gap-2">
                  <IconDeviceDesktop size={24} className="text-restro-green" /> Discovered OS Printers
                </h4>
                <p className="text-xs text-gray-500">
                  Printers installed in your operating system (USB thermal printers, CUPS, or Windows Spooler).
                </p>
              </div>

              <button
                onClick={fetchPrinters}
                disabled={loading}
                className="flex items-center gap-2 btn btn-outline btn-sm rounded-lg border-restro-border-green w-fit font-semibold"
              >
                <IconRefresh size={16} className={loading ? "animate-spin" : ""} /> Refresh OS Printers
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {printers.length === 0 && !loading ? (
                <div className="col-span-full text-center py-8 bg-gray-50 dark:bg-black/30 rounded-2xl border border-dashed border-gray-300">
                  <p className="text-gray-500">
                    {isElectron 
                      ? "No local OS printers discovered. Make sure printers are added in your operating system printer settings."
                      : "No printers discovered. Make sure the local print agent is running."
                    }
                  </p>
                </div>
              ) : (
                printers.map((printer, index) => {
                  const isCurrentDefault = defaultBillPrinter === printer.name;
                  const isTesting = testingPrinter === printer.name;
                  return (
                    <div
                      key={index}
                      className={`p-5 bg-white dark:bg-restro-gray border rounded-2xl flex flex-col justify-between gap-4 shadow-sm transition hover:shadow-md ${
                        isCurrentDefault ? "border-emerald-500 ring-2 ring-emerald-500/20" : "border-restro-border-green"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-xl bg-restro-border-green-light flex items-center justify-center text-restro-green-dark flex-shrink-0">
                          <SafeIconPrinter size={24} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold truncate text-base" title={printer.name}>
                            {printer.displayName || printer.name}
                          </h4>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="w-2 h-2 rounded-full bg-green-500"></span>
                            <span className="text-[10px] uppercase font-semibold text-gray-500">
                              {printer.isDefault ? "OS System Default" : "Local / USB Queue"}
                            </span>
                          </div>
                          {isCurrentDefault && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600 mt-1">
                              <IconStar size={12} className="fill-emerald-500" /> Default Bill Printer
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 pt-2 border-t border-gray-100 dark:border-gray-800">
                        <button
                          onClick={() => handleTestPrint({
                            name: printer.name,
                            isIp: false,
                            format: pageFormat
                          })}
                          disabled={isTesting}
                          className="flex-1 btn btn-outline btn-xs rounded-lg text-restro-green-dark border-restro-border-green hover:bg-restro-button-hover font-semibold flex items-center justify-center gap-1"
                        >
                          <IconSend size={14} className={isTesting ? "animate-spin" : ""} />
                          {isTesting ? "Printing..." : "Test Print"}
                        </button>

                        {!isCurrentDefault && (
                          <button
                            onClick={() => handleSetDefaultBillPrinter(printer.name)}
                            className="btn btn-outline btn-xs rounded-lg text-emerald-700 border-emerald-300 hover:bg-emerald-50 font-semibold"
                            title="Set as Default Bill Printer"
                          >
                            Set Default
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Web Bluetooth Configuration (Browser Mode Only) */}
          {!isElectron && (
            <div className="p-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-2xl">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h4 className="font-bold mb-1 flex items-center gap-2 text-blue-800 dark:text-blue-300">
                    <IconBluetooth size={22} /> Direct Bluetooth Printing (Web Bluetooth)
                  </h4>
                  <p className="text-sm text-blue-700 dark:text-blue-400">
                    Connect directly to a Bluetooth thermal printer from your browser (Chrome/Edge on Android, Windows, Mac).
                  </p>
                </div>
                
                <div className="flex items-center gap-3">
                  {btConnected ? (
                    <div className="flex flex-col items-end">
                      <span className="text-sm font-bold text-green-600 flex items-center gap-1">
                        <IconCheck size={16} /> Connected to: {btDeviceName}
                      </span>
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={async () => {
                            try {
                              toast.loading("Sending test page...");
                              await printViaWebBluetooth(null, "================================\n       BLUETOOTH TEST PRINT     \n================================\nStatus: Success\n================================\n\n\n");
                              toast.dismiss();
                              toast.success("Printed successfully");
                            } catch (e) {
                              toast.dismiss();
                              toast.error(`Test print failed: ${e.message}`);
                            }
                          }}
                          className="btn btn-outline btn-xs rounded-lg text-blue-700 border-blue-300 hover:bg-blue-100 dark:hover:bg-blue-800"
                        >
                          Test Print
                        </button>
                        <button
                          onClick={() => {
                            const device = getBluetoothDevice();
                            if (device && device.gatt.connected) device.gatt.disconnect();
                          }}
                          className="btn btn-outline btn-xs rounded-lg text-red-600 border-red-300 hover:bg-red-50 dark:hover:bg-red-900"
                        >
                          Disconnect
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={connectBluetoothPrinter}
                      className="rounded-lg transition active:scale-95 hover:shadow-lg px-4 py-2 text-white border border-blue-600 bg-blue-600 hover:bg-blue-700 font-semibold text-sm flex items-center gap-2"
                    >
                      <IconBluetooth size={18} /> Pair Bluetooth Printer
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ───────────────────────────────────────────────────────────── */}
      {/* MODAL: ADD / EDIT PRINTER TYPE (Matching Flutter KDS Dialog)  */}
      {/* ───────────────────────────────────────────────────────────── */}
      {showPrinterTypeModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white dark:bg-restro-gray border border-restro-border-green rounded-2xl w-full max-w-lg p-6 shadow-2xl">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-gray-800">
              <h3 className="text-lg font-bold flex items-center gap-2 text-restro-green-dark dark:text-emerald-300">
                <SafeIconPrinter size={22} />
                {editingPrinterType 
                  ? (editingPrinterType.id === DEFAULT_PRINTER_TYPE_ID ? "Configure Default Printer Type" : "Edit Printer Type")
                  : "Add New Printer Type"}
              </h3>
              <button
                onClick={() => setShowPrinterTypeModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-white text-xl font-bold"
              >
                ✕
              </button>
            </div>

            <div className="my-4 space-y-4 text-sm">
              {typeForm.id !== DEFAULT_PRINTER_TYPE_ID && (
                <div>
                  <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-1">
                    Printer Type Name * <span className="text-xs font-normal text-gray-500">(e.g. Kitchen Veg, Kitchen Non-Veg, Bar)</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Kitchen Veg"
                    value={typeForm.name}
                    onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })}
                    className="w-full rounded-lg px-3 py-2 border border-restro-border-green dark:bg-black font-semibold focus:outline-none"
                  />
                </div>
              )}

              <div>
                <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-1">Connection Type</label>
                <div className="grid grid-cols-2 gap-3 mt-1">
                  <label className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition ${typeForm.isOsPrinter ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/20 font-bold' : 'border-gray-200 dark:border-gray-800'}`}>
                    <input
                      type="radio"
                      name="connType"
                      checked={typeForm.isOsPrinter}
                      onChange={() => setTypeForm({ ...typeForm, isOsPrinter: true })}
                      className="radio radio-xs radio-success"
                    />
                    <span>OS / USB Printer</span>
                  </label>
                  <label className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition ${!typeForm.isOsPrinter ? 'border-teal-500 bg-teal-50/50 dark:bg-teal-950/20 font-bold' : 'border-gray-200 dark:border-gray-800'}`}>
                    <input
                      type="radio"
                      name="connType"
                      checked={!typeForm.isOsPrinter}
                      onChange={() => setTypeForm({ ...typeForm, isOsPrinter: false })}
                      className="radio radio-xs radio-info"
                    />
                    <span>WiFi / IP Network</span>
                  </label>
                </div>
              </div>

              {typeForm.isOsPrinter ? (
                <div>
                  <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-1">Select OS / USB Printer</label>
                  <select
                    value={typeForm.osPrinterName}
                    onChange={(e) => setTypeForm({ ...typeForm, osPrinterName: e.target.value })}
                    className="w-full rounded-lg px-3 py-2 border border-restro-border-green dark:bg-black font-semibold focus:outline-none"
                  >
                    <option value="">Default (System Default Printer)</option>
                    {printers.map((p, idx) => (
                      <option key={`os-pt-${idx}`} value={p.name}>
                        {p.displayName || p.name}
                      </option>
                    ))}
                  </select>
                  <div className="mt-2">
                    <span className="text-[11px] text-gray-400">Or enter printer queue name manually:</span>
                    <input
                      type="text"
                      placeholder="e.g. POS-80"
                      value={typeForm.osPrinterName}
                      onChange={(e) => setTypeForm({ ...typeForm, osPrinterName: e.target.value })}
                      className="w-full mt-1 rounded-lg px-3 py-1.5 text-xs border border-gray-200 dark:border-gray-800 dark:bg-black font-mono focus:outline-none"
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-1">Printer IP Address *</label>
                  <input
                    type="text"
                    placeholder="e.g. 192.168.1.100"
                    value={typeForm.ipPrinterAddress}
                    onChange={(e) => setTypeForm({ ...typeForm, ipPrinterAddress: e.target.value })}
                    className="w-full rounded-lg px-3 py-2 border border-restro-border-green dark:bg-black font-mono font-semibold focus:outline-none"
                  />
                  {networkPrinters.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5 items-center">
                      <span className="text-[11px] text-gray-400">Discovered IPs:</span>
                      {networkPrinters.map((np, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => setTypeForm({ ...typeForm, ipPrinterAddress: np.ip })}
                          className="btn btn-xs btn-outline rounded-md text-[10px]"
                        >
                          {np.name} ({np.ip})
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-1">Paper Roll Format</label>
                  <div className="flex gap-2">
                    <label className={`flex-1 flex items-center justify-center p-2 rounded-lg border cursor-pointer text-xs ${typeForm.paperFormat === '58' ? 'border-amber-500 bg-amber-50/50 dark:bg-amber-950/20 font-bold' : 'border-gray-200 dark:border-gray-800'}`}>
                      <input
                        type="radio"
                        name="fmt"
                        checked={typeForm.paperFormat === '58'}
                        onChange={() => setTypeForm({ ...typeForm, paperFormat: '58' })}
                        className="sr-only"
                      />
                      <span>58mm (2")</span>
                    </label>
                    <label className={`flex-1 flex items-center justify-center p-2 rounded-lg border cursor-pointer text-xs ${typeForm.paperFormat === '80' ? 'border-teal-500 bg-teal-50/50 dark:bg-teal-950/20 font-bold' : 'border-gray-200 dark:border-gray-800'}`}>
                      <input
                        type="radio"
                        name="fmt"
                        checked={typeForm.paperFormat === '80'}
                        onChange={() => setTypeForm({ ...typeForm, paperFormat: '80' })}
                        className="sr-only"
                      />
                      <span>80mm (3")</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-1">KOT Print Copies</label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setTypeForm({ ...typeForm, copies: Math.max(1, (typeForm.copies || 1) - 1) })}
                      disabled={typeForm.copies <= 1}
                      className="btn btn-sm btn-outline rounded-lg px-2"
                    >
                      -
                    </button>
                    <span className="font-bold text-base px-2">
                      {typeForm.copies || 1} {typeForm.copies === 1 ? 'Copy' : 'Copies'}
                    </span>
                    <button
                      type="button"
                      onClick={() => setTypeForm({ ...typeForm, copies: Math.min(10, (typeForm.copies || 1) + 1) })}
                      disabled={typeForm.copies >= 10}
                      className="btn btn-sm btn-outline rounded-lg px-2"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100 dark:border-gray-800">
              <button
                type="button"
                onClick={() => setShowPrinterTypeModal(false)}
                className="btn btn-ghost btn-sm rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSavePrinterType}
                className="btn btn-sm rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold flex items-center gap-1 shadow"
              >
                <IconCheck size={16} /> Save Printer Type
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ───────────────────────────────────────────────────────────── */}
      {/* MODAL: ADD MANUAL IP / NETWORK PRINTER                       */}
      {/* ───────────────────────────────────────────────────────────── */}
      {showAddIpModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white dark:bg-restro-gray border border-restro-border-green rounded-2xl w-full max-w-md p-6 shadow-2xl">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-gray-800">
              <h3 className="text-lg font-bold flex items-center gap-2 text-teal-800 dark:text-teal-300">
                <IconWifi size={22} /> Add Network / WiFi Printer
              </h3>
              <button
                onClick={() => setShowAddIpModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-white"
              >
                ✕
              </button>
            </div>

            <div className="my-4 space-y-4 text-sm">
              <div>
                <label className="block text-gray-500 font-medium mb-1">Printer Friendly Name</label>
                <input
                  type="text"
                  placeholder="e.g. Kitchen Thermal, Bar WiFi Printer"
                  value={ipForm.name}
                  onChange={(e) => setIpForm({ ...ipForm, name: e.target.value })}
                  className="w-full rounded-lg px-3 py-2 border border-restro-border-green dark:bg-black focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-gray-500 font-medium mb-1">IP Address *</label>
                <input
                  type="text"
                  placeholder="e.g. 192.168.1.100"
                  value={ipForm.ip}
                  onChange={(e) => setIpForm({ ...ipForm, ip: e.target.value })}
                  className="w-full rounded-lg px-3 py-2 border border-restro-border-green dark:bg-black focus:outline-none font-mono"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-gray-500 font-medium mb-1">Port</label>
                  <input
                    type="number"
                    placeholder="9100"
                    value={ipForm.port}
                    onChange={(e) => setIpForm({ ...ipForm, port: e.target.value })}
                    className="w-full rounded-lg px-3 py-2 border border-restro-border-green dark:bg-black focus:outline-none font-mono"
                  />
                  <span className="text-[10px] text-gray-400">Default: 9100</span>
                </div>

                <div>
                  <label className="block text-gray-500 font-medium mb-1">Paper Roll Format</label>
                  <select
                    value={ipForm.format}
                    onChange={(e) => setIpForm({ ...ipForm, format: e.target.value })}
                    className="w-full rounded-lg px-3 py-2 border border-restro-border-green dark:bg-black focus:outline-none font-semibold"
                  >
                    <option value="80">80mm Roll</option>
                    <option value="58">58mm Roll</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100 dark:border-gray-800">
              <button
                onClick={() => setShowAddIpModal(false)}
                className="btn btn-ghost btn-sm rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleVerifyAndSaveIpPrinter}
                disabled={verifyingIp}
                className="btn btn-sm rounded-lg bg-teal-600 hover:bg-teal-700 text-white flex items-center gap-1 font-semibold"
              >
                <IconCheck size={16} />
                {verifyingIp ? "Verifying..." : "Save Printer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}
